import { exhaustiveCheck } from '@ardoq/common-helpers';
import { selectionApi } from '@ardoq/api';
import {
  type FetchSelectionPayload,
  type UnifiedFetchSelection,
} from './types';
import { isUnifiedIntegrationId } from 'integrations/unified/utils';
import type {
  SelectionResponse,
  SelectionResponseTable,
} from '@ardoq/api-types/integrations';
import { ROW_INDEX_FIELD } from '../tablePreviews/constants';
import type { SelectionStateData } from './types';

const isUnifiedFetchSelection = (
  selectionPayload: FetchSelectionPayload
): selectionPayload is UnifiedFetchSelection =>
  isUnifiedIntegrationId(selectionPayload.integrationId);

type FetchRequestParams = Parameters<
  typeof selectionApi.fetchSelectionAsync
>[0];

export function selectionPayloadToFetchRequestParams(
  selectionPayload: FetchSelectionPayload
): FetchRequestParams {
  if (isUnifiedFetchSelection(selectionPayload)) {
    return {
      requestId: selectionPayload.requestId,
      integrationType: selectionPayload.integrationId,
      fetchRequestPayload: selectionPayload.payload,
    };
  }
  switch (selectionPayload.integrationId) {
    case 'aws-v3':
      return {
        requestId: selectionPayload.requestId,
        integrationType: 'aws',
        fetchRequestPayload: selectionPayload.payload,
      };
    case 'azure-v3':
      return {
        requestId: selectionPayload.requestId,
        integrationType: 'azure',
        fetchRequestPayload: selectionPayload.payload,
      };
    case 'servicenow-v3':
      return {
        requestId: selectionPayload.requestId,
        integrationType: 'servicenow',
        fetchRequestPayload: selectionPayload.payload,
      };
  }
  exhaustiveCheck(selectionPayload);
}

const normalizeSelectionResponseTable = (
  table: SelectionResponseTable
): SelectionResponseTable => {
  const { columns, sourceFieldNames, sourceFieldHeaders } = table;

  // If columns exist, derive sourceFieldNames/sourceFieldHeaders if missing
  if (columns && columns.length > 0) {
    return {
      ...table,
      sourceFieldNames: sourceFieldNames ?? columns.map(col => col.id),
      sourceFieldHeaders: sourceFieldHeaders ?? columns.map(col => col.header),
    };
  }

  // If sourceFieldNames exist but columns is missing, derive columns
  if (sourceFieldNames && sourceFieldNames.length > 0) {
    return {
      ...table,
      sourceFieldHeaders: sourceFieldHeaders ?? sourceFieldNames,
      columns: sourceFieldNames.map((id, index) => ({
        index,
        id,
        header: sourceFieldHeaders?.[index] ?? id,
      })),
    };
  }

  return table;
};

export const normalizeSelectionResponse = <T extends SelectionResponse>(
  response: T
): T => ({
  ...response,
  tables: response.tables.map(normalizeSelectionResponseTable),
});

export const prependRowIndexField = (headers: string[]): string[] => [
  ROW_INDEX_FIELD,
  ...headers,
];

export const getSelectionTables = (
  selectionState: SelectionStateData
): SelectionResponseTable[] => {
  if (selectionState.status !== 'SUCCESS') {
    return [];
  }
  return selectionState.data.tables;
};

export const getSelectionTablesById = (
  selectionState: SelectionStateData
): Record<string, SelectionResponseTable> =>
  Object.fromEntries(
    getSelectionTables(selectionState).map(table => [table.id, table])
  );
